/**
 * Frontend Map Utilities (Leaflet)
 * 
 * Provides functions for initializing and interacting with Leaflet maps,
 * handling markers, popups, layers, and tile providers.
 */

import L from 'leaflet';
import 'leaflet/dist/leaflet.css';

// --- Configuration (Consider moving to a central config file/context) ---

// Default map settings
const DEFAULT_CENTER = [39.8283, -98.5795]; // Center of the US
const DEFAULT_ZOOM = 4;

// Tile Layer Providers (URLs and Attributions)
// Ensure API keys are handled securely (e.g., via backend proxy or build-time variables)
// Avoid committing keys directly here.
const TILE_PROVIDERS = {
  // Esri's free community basemap (server.arcgisonline.com), no API key.
  // tile.openstreetmap.org and CARTO's basemap CDN (both below) were tried
  // first and both now refuse anonymous production traffic — OSM's own
  // server 403s with an "Access blocked" tile, and CARTO now returns an
  // "API KEY REQUIRED" watermark tile instead of map data on every request
  // (confirmed 2026-09-28 by opening the actual PNG each returns, not just
  // its HTTP status — a 200 response from either one is not proof of a real
  // tile). Esri's tiles were checked the same way and are real map data.
  esri: {
    url: 'https://server.arcgisonline.com/ArcGIS/rest/services/World_Street_Map/MapServer/tile/{z}/{y}/{x}',
    options: {
      attribution: 'Tiles &copy; <a href="https://www.esri.com/">Esri</a>',
      maxZoom: 19,
    }
  },
  // Neither of these serves real tiles anonymously any more — kept only so
  // a future provider swap has the exact URLs already tried and ruled out.
  carto: {
    url: 'https://{s}.basemaps.cartocdn.com/rastertiles/voyager/{z}/{x}/{y}{r}.png',
    options: {
      attribution: '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors &copy; <a href="https://carto.com/attributions">CARTO</a>',
      subdomains: ['a', 'b', 'c', 'd'],
      maxZoom: 20,
    }
  },
  openstreetmap: {
    url: 'https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png',
    options: {
      attribution: '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors',
      subdomains: ['a', 'b', 'c'],
      maxZoom: 19,
    }
  },
  mapbox: {
    // Example using Mapbox Streets v11 - Replace with your style URL and access token
    // It's best practice to fetch the token/style URL from your backend or config
    url: `https://api.mapbox.com/styles/v1/mapbox/streets-v11/tiles/{z}/{x}/{y}?access_token=${process.env.REACT_APP_MAPBOX_API_KEY || 'YOUR_MAPBOX_ACCESS_TOKEN'}`,
    options: {
      attribution: 'Map data &copy; <a href="https://www.openstreetmap.org/">OpenStreetMap</a> contributors, <a href="https://creativecommons.org/licenses/by-sa/2.0/">CC-BY-SA</a>, Imagery &copy; <a href="https://www.mapbox.com/">Mapbox</a>',
      maxZoom: 19,
      tileSize: 512,
      zoomOffset: -1,
    }
  },
  here: {
    // Example using HERE Normal Day tiles - Replace with your API key
    // Best practice: Fetch the API key from backend/config
    url: `https://2.base.maps.ls.hereapi.com/maptile/2.1/maptile/newest/normal.day/{z}/{x}/{y}/512/png8?apiKey=${process.env.REACT_APP_HERE_API_KEY || 'YOUR_HERE_API_KEY'}`,
    options: {
      attribution: 'Map data &copy; <a href="https://developer.here.com">HERE</a>',
      maxZoom: 19,
      tileSize: 512,
    }
  },
  // Add other providers like MapTiler, Stadia Maps, etc. if needed
};

// Default provider if none specified
const DEFAULT_TILE_PROVIDER = 'esri';

// --- Map Initialization ---

/**
 * Initializes a Leaflet map instance on a given DOM element ID.
 * 
 * @param {string} mapId - The ID of the DOM element to attach the map to.
 * @param {object} options - Leaflet map options (center, zoom, etc.).
 * @param {string} tileProviderName - Name of the tile provider to use (e.g., 'openstreetmap').
 * @returns {L.Map} - The initialized Leaflet map instance.
 */
export const initializeMap = (mapId, options = {}, tileProviderName = DEFAULT_TILE_PROVIDER) => {
  const mapOptions = {
    center: options.center || DEFAULT_CENTER,
    zoom: options.zoom || DEFAULT_ZOOM,
    ...options, // Allow overriding defaults
  };

  const map = L.map(mapId, mapOptions);

  // Add the selected tile layer
  addTileLayer(map, tileProviderName);

  // Fix for default icon path issues with bundlers like Webpack
  fixDefaultIconPaths();

  return map;
};

/**
 * Adds a specified tile layer to the map.
 * 
 * @param {L.Map} map - The Leaflet map instance.
 * @param {string} providerName - The name of the tile provider (key in TILE_PROVIDERS).
 * @returns {L.TileLayer | null} The added tile layer instance, or null if provider not found.
 */
export const addTileLayer = (map, providerName = DEFAULT_TILE_PROVIDER) => {
  let provider = TILE_PROVIDERS[providerName];
  if (!provider) {
    console.error(`Tile provider "${providerName}" not found. Using default.`);
    providerName = DEFAULT_TILE_PROVIDER; // Fallback
    provider = TILE_PROVIDERS[providerName];
    if (!provider) {
      console.error('Default tile provider not found either!');
      return null; // Cannot add layer
    }
  }

  // Basic check for API key placeholders (in a real app, handle this more robustly)
  if (provider.url.includes('YOUR_') && (providerName === 'mapbox' || providerName === 'here')) {
     console.warn(`API Key for ${providerName} seems to be missing or is a placeholder. Map tiles may not load.`);
  }

  const tileLayer = L.tileLayer(provider.url, provider.options);
  tileLayer.addTo(map);
  return tileLayer;
};

/**
 * Fixes the default Leaflet icon paths which can break with module bundlers.
 */
export const fixDefaultIconPaths = () => {
  delete L.Icon.Default.prototype._getIconUrl;
  L.Icon.Default.mergeOptions({
    iconRetinaUrl: require('leaflet/dist/images/marker-icon-2x.png'),
    iconUrl: require('leaflet/dist/images/marker-icon.png'),
    shadowUrl: require('leaflet/dist/images/marker-shadow.png'),
  });
};


// --- Markers and Popups ---

/**
 * Adds a marker to the map.
 * 
 * @param {L.Map} map - The Leaflet map instance.
 * @param {Array<number>} coordinates - [latitude, longitude] for the marker.
 * @param {object} options - Leaflet marker options (icon, title, etc.).
 * @param {string} [popupContent] - Optional HTML content for a popup bound to the marker.
 * @returns {L.Marker} - The created marker instance.
 */
export const addMarker = (map, coordinates, options = {}, popupContent = null) => {
  if (!Array.isArray(coordinates) || coordinates.length < 2) {
    console.error('Invalid coordinates for addMarker:', coordinates);
    return null;
  }
  const marker = L.marker(coordinates, options).addTo(map);

  if (popupContent) {
    marker.bindPopup(popupContent);
  }

  return marker;
};

/**
 * Creates a custom Leaflet icon.
 * 
 * @param {object} options - Options for L.icon (iconUrl, iconSize, etc.).
 * @returns {L.Icon} - The created custom icon instance.
 */
export const createCustomIcon = (options) => {
  return L.icon(options);
};

/**
 * Updates the position of an existing marker.
 * 
 * @param {L.Marker} marker - The marker instance to update.
 * @param {Array<number>} newCoordinates - The new [latitude, longitude].
 */
export const updateMarkerPosition = (marker, newCoordinates) => {
  if (marker && marker.setLatLng && Array.isArray(newCoordinates) && newCoordinates.length >= 2) {
    marker.setLatLng(newCoordinates);
  } else {
    console.error('Invalid marker or coordinates for updateMarkerPosition');
  }
};

/**
 * Removes a marker from the map.
 * 
 * @param {L.Map} map - The Leaflet map instance.
 * @param {L.Marker} marker - The marker instance to remove.
 */
export const removeMarker = (map, marker) => {
  if (map && marker && map.hasLayer(marker)) {
    map.removeLayer(marker);
  }
};

// --- Map Views and Bounds ---

/**
 * Sets the map view to specific coordinates and zoom level.
 * 
 * @param {L.Map} map - The Leaflet map instance.
 * @param {Array<number>} coordinates - [latitude, longitude] to center on.
 * @param {number} zoom - The zoom level.
 * @param {object} [options] - Leaflet pan/zoom options (e.g., { animate: true }).
 */
export const setMapView = (map, coordinates, zoom, options = {}) => {
  if (map && map.setView && Array.isArray(coordinates) && coordinates.length >= 2) {
    map.setView(coordinates, zoom || map.getZoom(), options);
  }
};

/**
 * Fits the map view to contain a given bounds or set of markers.
 * 
 * @param {L.Map} map - The Leaflet map instance.
 * @param {L.LatLngBounds | Array<L.Marker> | Array<Array<number>>} target - 
 *        A LatLngBounds object, an array of markers, or an array of [lat, lng] coordinates.
 * @param {object} [options] - Leaflet fitBounds options (e.g., { padding: [50, 50] }).
 */
export const fitMapToBounds = (map, target, options = {}) => {
  if (!map || !target) return;

  let bounds;

  if (target instanceof L.LatLngBounds) {
    bounds = target;
  } else if (Array.isArray(target)) {
    if (target.length === 0) return; // Nothing to fit

    // Check if it's an array of markers or coordinates
    if (target[0] instanceof L.Marker) {
      const featureGroup = L.featureGroup(target);
      bounds = featureGroup.getBounds();
    } else if (Array.isArray(target[0]) && target[0].length >= 2) {
      bounds = L.latLngBounds(target);
    }
  }

  if (bounds && bounds.isValid()) {
    map.fitBounds(bounds, options);
  } else {
    console.warn('Could not determine valid bounds for fitMapToBounds');
  }
};

// --- Drawing and Geometry ---

/**
 * Draws a polyline (route) on the map.
 * 
 * @param {L.Map} map - The Leaflet map instance.
 * @param {Array<Array<number>>} coordinates - An array of [latitude, longitude] points.
 * @param {object} options - Leaflet polyline options (color, weight, etc.).
 * @returns {L.Polyline} - The created polyline instance.
 */
export const drawPolyline = (map, coordinates, options = {}) => {
  if (!map || !Array.isArray(coordinates) || coordinates.length < 2) {
     console.error('Invalid input for drawPolyline');
     return null;
  }
  const polyline = L.polyline(coordinates, options).addTo(map);
  return polyline;
};

/**
 * Draws a GeoJSON feature collection on the map.
 * 
 * @param {L.Map} map - The Leaflet map instance.
 * @param {object} geoJsonData - GeoJSON FeatureCollection or Feature object.
 * @param {object} options - Leaflet GeoJSON layer options (style, onEachFeature, pointToLayer).
 * @returns {L.GeoJSON} - The created GeoJSON layer instance.
 */
export const drawGeoJson = (map, geoJsonData, options = {}) => {
  if (!map || !geoJsonData) {
     console.error('Invalid input for drawGeoJson');
     return null;
  }
  const geoJsonLayer = L.geoJSON(geoJsonData, options).addTo(map);
  return geoJsonLayer;
};

/**
 * Removes a layer (like a polyline or GeoJSON layer) from the map.
 * 
 * @param {L.Map} map - The Leaflet map instance.
 * @param {L.Layer} layer - The layer instance to remove.
 */
export const removeLayer = (map, layer) => {
  if (map && layer && map.hasLayer(layer)) {
    map.removeLayer(layer);
  }
};

// Removed duplicate export statement and extra braces
// --- Utility ---

/**
 * Converts GeoJSON coordinates [lng, lat] to Leaflet LatLng [lat, lng].
 * Handles Point, LineString, and Polygon coordinate structures.
 * 
 * @param {Array} geoJsonCoords - GeoJSON coordinates array.
 * @returns {Array | Array<Array> | Array<Array<Array>>} - Leaflet compatible coordinates.
 */
export const geoJsonCoordsToLeaflet = (geoJsonCoords) => {
  if (!Array.isArray(geoJsonCoords)) return geoJsonCoords;

  // Simple check for Point [lng, lat]
  if (geoJsonCoords.length === 2 && typeof geoJsonCoords[0] === 'number' && typeof geoJsonCoords[1] === 'number') {
    return [geoJsonCoords[1], geoJsonCoords[0]]; // Swap to [lat, lng]
  }

  // Check for LineString [[lng, lat], ...] or Polygon [[lng, lat], ...]
  if (Array.isArray(geoJsonCoords[0]) && geoJsonCoords[0].length === 2) {
    return geoJsonCoords.map(coord => [coord[1], coord[0]]); // Swap each point
  }
  
  // Check for MultiPolygon or Polygon with holes [[[lng, lat], ...], ...]
  if (Array.isArray(geoJsonCoords[0]) && Array.isArray(geoJsonCoords[0][0]) && geoJsonCoords[0][0].length === 2) {
     return geoJsonCoords.map(ring => ring.map(coord => [coord[1], coord[0]])); // Swap each point in each ring
  }

  console.warn("Unrecognized GeoJSON coordinate structure for conversion:", geoJsonCoords);
  return geoJsonCoords; // Return original if structure is unknown
};